/**
 * Inline floating Feedback Widget.
 *
 * Always-on for beta users (is_beta = '1' in localStorage); also available
 * to regular authed users as a "Send feedback" CTA. Renders as a small
 * floating button in the bottom-right; opens a 5-star + message panel.
 *
 * Posts to POST /api/beta/feedback with the current path.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Star, X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAuthed = !!localStorage.getItem('token');
  const isBeta = localStorage.getItem('is_beta') === '1';

  // Only render on authenticated routes (the widget needs a JWT to POST).
  // Hide on public marketing pages so it doesn't clutter conversion paths.
  if (!isAuthed) return null;
  const skipPaths = ['/login', '/beta', '/pricing'];
  if (skipPaths.some((p) => location.pathname.startsWith(p))) return null;

  const submit = async () => {
    if (!message.trim() || message.trim().length < 2) {
      toast.error('Please share a quick thought.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/beta/feedback`,
        { page: location.pathname, rating, message },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success('Thanks for the feedback! 💚');
      setMessage('');
      setRating(5);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[55] bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full shadow-lg shadow-emerald-500/40 px-4 py-2.5 font-semibold text-sm flex items-center gap-2 transition-all hover:scale-105"
          data-testid="feedback-widget-fab"
          title="Send feedback to the CARTIEAI team"
          aria-label="Send feedback"
        >
          <MessageSquare className="w-4 h-4" />
          {isBeta ? 'Beta Feedback' : 'Feedback'}
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-[55] w-[min(380px,90vw)] bg-slate-900 border border-emerald-500/30 rounded-xl shadow-2xl shadow-emerald-500/20 p-5"
          data-testid="feedback-widget-panel"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="text-base font-bold text-white">Quick feedback</div>
                {isBeta && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">BETA</Badge>}
              </div>
              <div className="text-xs text-slate-300 mt-0.5">on <span className="text-emerald-300 font-mono">{location.pathname}</span></div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-300 hover:text-white" data-testid="feedback-close-btn" aria-label="Close feedback">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mb-3">
            <div className="text-xs text-slate-300 font-semibold mb-1.5">How's this page?</div>
            <div className="flex gap-1" data-testid="feedback-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  className="transition-transform hover:scale-110"
                  data-testid={`feedback-star-${n}`}
                  aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
                >
                  <Star className={`w-6 h-6 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-700'}`} />
                </button>
              ))}
            </div>
          </div>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What worked? What confused you? Brutal honesty appreciated."
            rows={3}
            className="bg-slate-950 border-slate-700 text-sm text-slate-200 placeholder:text-slate-400 mb-3"
            data-testid="feedback-message-input"
            maxLength={2000}
          />

          <Button
            onClick={submit}
            disabled={submitting || message.trim().length < 2}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            data-testid="feedback-submit-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? 'Sending…' : 'Send to CARTIE team'}
          </Button>
          <p className="text-[11px] text-slate-300 mt-2 text-center">Your feedback goes straight to the founder · We read every word.</p>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
